import React, { useState } from 'react';
import { authApiClient, safeReturnPath } from './AuthApiClient';
import { useProductAuth } from './ProductAuthProvider';
import { navigateProduct } from '../shell/routes';
import './account-page.css';

/** Signup, email login and the existing Google PKCE flow share one entry. */
export function AccountPage() {
  const auth = useProductAuth();
  const query = new URLSearchParams(window.location.search);
  const returnTo = safeReturnPath(query.get('returnTo'));
  const [mode, setMode] = useState<'login' | 'signup'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mailSent, setMailSent] = useState<boolean | null>(null);
  const signedIn = auth.phase === 'authenticated';
  const verified = signedIn && auth.user?.email_verified === true;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      if (mode === 'signup') {
        const result = await authApiClient.registerWithEmail({ email: email.trim(), password,
          first_name: name.trim(), next_url: `${window.location.origin}/auth?returnTo=${encodeURIComponent(returnTo)}` });
        setMailSent(result.mailSent);
      } else {
        await authApiClient.signInWithEmail(email, password);
      }
      setPassword('');
      await auth.refresh();
      if (mode === 'login') navigateProduct(returnTo, { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Anmeldung fehlgeschlagen.');
    } finally { setBusy(false); }
  };
  const google = async () => {
    setBusy(true); setError(null);
    try { await authApiClient.beginLogin(returnTo); }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Anmeldung fehlgeschlagen.'); setBusy(false); }
  };
  return <main className="ps-account-page">
    <a href="/" className="ps-account-brand">3D Presenter</a>
    <section className="ps-account-card">
      <h1>{verified ? 'Dein Konto ist bereit.' : mailSent !== null ? 'Dein Konto ist angelegt.' : signedIn ? 'Bestätige deine E-Mail-Adresse.' : mode === 'signup' ? 'Deine Geschichten bekommen Raum.' : 'Willkommen zurück.'}</h1>
      {(error || auth.error) && <p role="alert" className="ps-alert">{error || auth.error}</p>}
      {signedIn || mailSent !== null ? <>
        {verified ? <p>Deine E-Mail-Adresse ist bestätigt. Du kannst jetzt Cloud-Speichern und Teilen verwenden.</p> : <>
        <p>{mailSent ? `Wir haben eine Bestätigung an ${email} gesendet. Öffne den Link in der E-Mail, um Cloud-Speichern und Teilen freizuschalten.`
          : mailSent === false ? 'Die Bestätigungsmail konnte nicht versendet werden. Cloud-Zugriff bleibt gesperrt; du kannst lokal weiterarbeiten.'
          : 'Öffne die Bestätigungsmail für dein Konto. Bis dahin kannst du lokal weiterarbeiten.'}</p>
        <button className="ps-button ps-button-quiet" disabled={auth.phase === 'loading'} onClick={() => void auth.refresh()}>Bestätigung prüfen</button>
        </>}
        <button className="ps-button ps-button-primary" onClick={() => navigateProduct(returnTo)}>Zum Entwurf</button>
      </> : <>
        <p>Lokal kannst du sofort gestalten. Mit einem bestätigten Konto speicherst und teilst du deine Präsentationen in der Cloud.</p>
        <div className="ps-account-tabs" aria-label="Konto-Zugang">
          <button aria-pressed={mode === 'signup'} disabled={busy} onClick={() => { setMode('signup'); setError(null); }}>Konto erstellen</button>
          <button aria-pressed={mode === 'login'} disabled={busy} onClick={() => { setMode('login'); setError(null); }}>Anmelden</button>
        </div>
        <form onSubmit={submit}>
          {mode === 'signup' && <label>Dein Name<input autoComplete="given-name" required value={name} onChange={e => setName(e.target.value)} maxLength={120} /></label>}
          <label>E-Mail<input type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} maxLength={254} /></label>
          <label>Passwort<input type="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} required minLength={8} maxLength={200} value={password} onChange={e => setPassword(e.target.value)} /></label>
          <button className="ps-button ps-button-primary" disabled={busy}>{busy ? 'Einen Moment …' : mode === 'signup' ? 'Konto erstellen' : 'Anmelden'}</button>
        </form>
        <button className="ps-button ps-button-quiet" disabled={busy} onClick={() => void google()}>Mit Google fortfahren</button>
        <a href={returnTo}>Ohne Anmeldung lokal weiterarbeiten</a>
      </>}
    </section>
  </main>;
}

export function EmailVerificationNotice() {
  const auth = useProductAuth();
  const [checking, setChecking] = useState(false);
  if (auth.phase !== 'authenticated' || auth.user?.email_verified === true) return null;
  return <aside className="ps-verification-notice" role="status">
    <span>Bitte bestätige deine E-Mail-Adresse. Cloud-Speichern und Teilen bleiben bis dahin gesperrt; lokale Entwürfe bleiben erhalten.</span>
    <button className="ps-button ps-button-quiet" disabled={checking} onClick={async () => {
      setChecking(true); try { await auth.refresh(); } finally { setChecking(false); }
    }}>{checking ? 'Prüft …' : 'Bestätigung prüfen'}</button>
  </aside>;
}
